import FieldWeatherSound from './FieldWeatherSound';
import {
  FIELD_EFFECT_SOUND_IDS,
  FIELD_EFFECT_SOUND_KINDS,
  getFieldEffectSounds,
} from '../configs/fieldEffectSounds';
import {DUST_DEVIL_TAG, resolveDustDevilStrength} from '../utils/effects/biome/dustDevil';
import {
  GEYSER_ERUPTION_TAG,
  GEYSER_FUMAROLE_TAG,
  resolveGeyserEruptionStrength,
} from '../utils/effects/biome/geyserSteam';
import {LABYRINTH_GLOW_TAG} from '../utils/effects/biome/labyrinthGlow';
import {SAND_STREAMERS_TAG} from '../utils/effects/biome/sandStreamers';
import {SNOW_DRIFT_GUST_TAG} from '../utils/effects/biome/snowDrift';
import {SNOW_SQUALL_TAG, resolveSnowSquallStrength} from '../utils/effects/biome/snowSquall';
import {WIND_GUST_TAG} from '../utils/effects/biome/windGust';
import {WIND_VEILS_TAG} from '../utils/effects/biome/windVeils';

/**
 * ЗВУКИ ЭФФЕКТОВ ПОДЛОЖЕК: вьюга воет, пыльный вихрь шумит песком, порыв
 * поземки свистит, над лугом шелестит трава, гейзер шипит паром. Что чем звучит
 * и на какой подложке — configs/fieldEffectSounds (там же правила двух видов
 * привязок: «буря» и «порыв»); здесь — тот, кто эти звуки играет.
 *
 * КАК УСТРОЕНО. Эффект подложки ведет свой контроллер (scenes/FieldBiomeEffects),
 * и о звуке он не знает ничего. Дорожки эффекта, у которых есть звук, помечены
 * (tag); по метке отсюда каждый шаг спрашивают у контроллера живые группы
 * дорожки (findLiveGroups): сколько группа прожила и насколько она в кадре
 * игрока. Обратная связь одна: «буре» нельзя родиться, пока не загружен ее
 * звук, — об этом контроллер спрашивает через хозяина (checkIfTrackAllowed).
 *
 * «БУРЯ» — ровно то, чем был вой вьюги до появления других звуков: звук ведет
 * FieldWeatherSound по силе эффекта (сильнейшей из живых групп), в кадре
 * эффект или нет.
 *
 * «ПОРЫВ». Появление — время, которое группа проводит в кадре игрока, вместе
 * с короткими отлучками из него. Зазвучит ли появление, решается в его первый
 * миг, и только тогда: играет один случайный ЗАГРУЖЕННЫЙ звук привязки (не
 * тот, что в прошлый раз, если есть из чего выбрать). Не зазвучало — появление
 * так и пройдет молча, а именно:
 *  - ни один звук привязки еще не загружен (или игре пока нельзя звучать);
 *  - звуков привязки уже звучит столько, сколько ей положено разом (maxVoices;
 *    обычно — один);
 *  - с начала прошлого звука прошло меньше положенной паузы (minPauseMs).
 * Звука посреди появления не бывает: шум ветра, начавшийся, когда порыв уже
 * полпути как идет, или шипение над струйкой пара, которая уже тает, звучали
 * бы невпопад — звук обязан совпасть с тем, что игрок увидел.
 * Дальше слышимость идет за группой, с которой звук начался: пока она в кадре
 * — быстро нарастает и держится, ушла из кадра или кончилась — плавно падает,
 * и в тишине звук обрывается; вернулась раньше — слышимость возвращается.
 * Падает и растет она по кривой привязки (levelPower): на слух — ровно.
 * Доигравший сам звук для того же появления заново не запускается.
 * Отлучка кончает появление, когда звук группы затих; у группы без звука
 * (молчавшей или уже отзвучавшей) — когда она пробыла вне кадра столько,
 * сколько звук затихает (fadeOutMs). Новое появление — другая группа или та
 * же, вернувшаяся после этого, — озвучивается заново.
 *
 * Привязки звучат независимо: вой вьюги и свист порыва поземки идут разом.
 *
 * Жребий здесь свой, а не общий с антуражем: звук не должен менять то, как
 * идут эффекты, плывут облака и летят птицы.
 *
 * Время — шаг времени кадра от FieldAmbience, тот же, что двигает картинку: в
 * скрытой вкладке стоит и то, и другое.
 *
 * Звуки — украшение украшения: сбой в них не должен задеть ни эффект, ни
 * антураж, поэтому наружу отсюда ничего не выходит. Сбой одной привязки
 * обрывает ее звук и остальных не касается.
 */

const {STORM, GUST} = FIELD_EFFECT_SOUND_KINDS;
const IDS = FIELD_EFFECT_SOUND_IDS;

/**
 * ЧТО ЗВУЧИТ: привязка из configs/fieldEffectSounds -> метка дорожки эффекта
 * (tag, из его построителя) и, где она нужна, сила эффекта (resolveStrength:
 * (группа, сколько она прожила) -> 0..1). У «бури» по силе идет слышимость; у
 * «порыва» сила — признак того, что эффект и вправду идет: группа извержения
 * гейзера живет дольше его струи, и облака пара без струи не шипят.
 */
export const FIELD_EFFECT_SOUND_SOURCES = Object.freeze({
  [IDS.SNOW_SQUALL]: Object.freeze({tag: SNOW_SQUALL_TAG, resolveStrength: resolveSnowSquallStrength}),
  [IDS.DUST_DEVIL]: Object.freeze({tag: DUST_DEVIL_TAG, resolveStrength: resolveDustDevilStrength}),
  [IDS.SNOW_DRIFT_GUST]: Object.freeze({tag: SNOW_DRIFT_GUST_TAG}),
  [IDS.SAND_STREAMERS]: Object.freeze({tag: SAND_STREAMERS_TAG}),
  [IDS.WIND_GUST]: Object.freeze({tag: WIND_GUST_TAG}),
  [IDS.WIND_VEILS]: Object.freeze({tag: WIND_VEILS_TAG}),
  [IDS.GEYSER_ERUPTION]: Object.freeze({tag: GEYSER_ERUPTION_TAG, resolveStrength: resolveGeyserEruptionStrength}),
  [IDS.GEYSER_FUMAROLE]: Object.freeze({tag: GEYSER_FUMAROLE_TAG}),
  [IDS.LABYRINTH_GLOW]: Object.freeze({tag: LABYRINTH_GLOW_TAG}),
});

/** Привязки подложки по перечням: числа и звуки из конфига, метка и сила — от построителя эффекта */
const resolveRegisteredBindings = (groundBackgroundId) => {
  const bindings = [];
  getFieldEffectSounds(groundBackgroundId).forEach((settings) => {
    const source = Object.prototype.hasOwnProperty.call(FIELD_EFFECT_SOUND_SOURCES, settings.id)
      ? FIELD_EFFECT_SOUND_SOURCES[settings.id]
      : null;
    // Привязке без дорожки звучать не над чем
    if (source) {
      bindings.push({...settings, ...source});
    }
  });
  return bindings;
};

// Звучащим звуком можно управлять, если SoundManager отдал его целиком
const SOUND_CONTROLS = ['checkIfPlaying', 'setLevel', 'stop'];
// Живых групп нет: общий ответ, чтобы не заводить новый на каждом кадре
const NO_GROUPS = Object.freeze([]);

/** Звук «бури»: идет за силой эффекта — так же, как шел вой вьюги (FieldWeatherSound) */
class StormSound {
  constructor(scene, binding) {
    this.binding = binding;
    this.follower = new FieldWeatherSound(scene, {soundId: binding.soundIds[0], fadeMs: binding.fadeMs});
  }

  /** Можно ли эффекту начаться: его звук загружен — или звука в этой сцене нет вовсе */
  checkIfReady() {
    return this.follower.checkIfReady();
  }

  update(stepMs, groups) {
    // Групп несколько (прежняя доживает после смены ветра) — звук идет за сильнейшей
    let strength = 0;
    for (let index = 0; index < groups.length; index += 1) {
      const groupStrength = this.binding.resolveStrength(groups[index].group, groups[index].elapsedMs);
      if (groupStrength > strength) {
        strength = groupStrength;
      }
    }
    this.follower.update(stepMs, strength);
  }

  /** Оборвать звук разом */
  stop() {
    this.follower.stop();
  }

  /** Поле перестроили: картинки больше нет — звук обрывается */
  reset() {
    this.follower.stop();
  }
}

/** Звук «порыва»: один случайный звук на появление группы в кадре игрока */
class GustSound {
  constructor(scene, binding, rng) {
    this.scene = scene;
    this.binding = binding;
    this.rng = rng;
    // Звучащие звуки привязки: {sound — от SoundManager.playDeferredSound,
    // level — его слышимость 0..1, owner — группа, с появлением которой он
    // начался}. Обычно звук один; привязке, у которой группы идут внахлест, а
    // звучать обязана каждая (струйки пара), разрешено несколько (maxVoices)
    this.voices = [];
    // Какой звук играл в прошлый раз и сколько времени кадров прошло с его начала
    this.lastSoundId = null;
    this.sinceStartMs = Infinity;
    // Идущие появления: {group, absentMs — сколько группа уже вне кадра, isLive
    // — жива ли она на этом шаге}. Групп у привязки считаные единицы
    this.appearances = [];
  }

  update(stepMs, groups) {
    if (!(stepMs > 0)) {
      return;
    }
    this.sinceStartMs += stepMs;
    // Доиграл сам или оборван извне — следить больше не за чем. Появление, с
    // которым он начался, при этом идет дальше: заново оно не зазвучит
    for (let index = this.voices.length - 1; index >= 0; index -= 1) {
      if (!this.voices[index].sound.checkIfPlaying()) {
        this.voices.splice(index, 1);
      }
    }
    this.followGroups(stepMs, groups);

    const {fadeInMs, fadeOutMs} = this.binding;
    for (let index = this.voices.length - 1; index >= 0; index -= 1) {
      const voice = this.voices[index];
      const isOwnerInView = this.checkIfOwnerInView(voice.owner);
      voice.level = Math.min(1, Math.max(0, voice.level + (isOwnerInView ? stepMs / fadeInMs : -stepMs / fadeOutMs)));
      if (!isOwnerInView && voice.level <= 0) {
        // Звук затих — появление кончилось: вернется группа в кадр, это будет новое
        this.forgetAppearance(voice.owner);
        this.voices.splice(index, 1);
        this.stopSound(voice.sound);
      } else {
        // На слух громкость не линейна: слышимость — доля в степени привязки (levelPower; нет ее — как есть)
        voice.sound.setLevel(voice.level ** (this.binding.levelPower || 1));
      }
    }
  }

  /** В кадре ли сейчас группа, с появлением которой начался звук */
  checkIfOwnerInView(owner) {
    const appearance = this.findAppearance(owner);
    return Boolean(appearance && appearance.isInView);
  }

  /**
   * Кто из групп сейчас в кадре (isInView появления): новые появления
   * начинаются (и, если можно, звучат), закончившиеся — забываются
   */
  followGroups(stepMs, groups) {
    const {appearances} = this;
    for (let index = 0; index < appearances.length; index += 1) {
      appearances[index].isLive = false;
      appearances[index].isInView = false;
    }

    for (let index = 0; index < groups.length; index += 1) {
      const {group} = groups[index];
      let appearance = this.findAppearance(group);
      if (this.checkIfInView(groups[index])) {
        if (!appearance) {
          appearance = {group, absentMs: 0, isLive: true, isInView: true};
          appearances.push(appearance);
          this.start(group);
        }
        appearance.absentMs = 0;
        appearance.isLive = true;
        appearance.isInView = true;
      } else if (appearance) {
        appearance.absentMs += stepMs;
        appearance.isLive = true;
      }
    }

    // Появление кончилось: группы больше нет — или она вне кадра столько,
    // сколько затихает звук: ее возвращение будет новым появлением. У группы,
    // чей звук затих раньше (он не успел набрать силу), появление кончается
    // вместе со звуком (см. update)
    for (let index = appearances.length - 1; index >= 0; index -= 1) {
      if (!appearances[index].isLive || appearances[index].absentMs >= this.binding.fadeOutMs) {
        appearances.splice(index, 1);
      }
    }
  }

  findAppearance(group) {
    for (let index = 0; index < this.appearances.length; index += 1) {
      if (this.appearances[index].group === group) {
        return this.appearances[index];
      }
    }
    return null;
  }

  forgetAppearance(group) {
    const index = this.appearances.findIndex((appearance) => appearance.group === group);
    if (index >= 0) {
      this.appearances.splice(index, 1);
    }
  }

  /** Видит ли игрок группу: она в кадре и достаточно плотна — и эффект в ней и вправду идет */
  checkIfInView({group, elapsedMs, inViewAlpha}) {
    const {minInViewAlpha, minStrength, resolveStrength} = this.binding;
    if (!(inViewAlpha >= minInViewAlpha)) {
      return false;
    }
    return typeof resolveStrength !== 'function' || resolveStrength(group, elapsedMs) >= minStrength;
  }

  /** Группа появилась в кадре: зазвучит ли это появление, решается сейчас — и только сейчас */
  start(group) {
    // Звуков привязки разом — не больше положенного (обычно один), а между
    // началами звуков — пауза: иначе появление пройдет молча
    if (this.voices.length >= (this.binding.maxVoices || 1) || this.sinceStartMs < this.binding.minPauseMs) {
      return;
    }
    const soundId = this.pickSoundId();
    if (soundId === null) {
      return;
    }
    // Неслышным: дальше его слышимость поднимает update
    const sound = this.scene?.soundManager?.playDeferredSound?.(soundId, {level: 0});
    // Звук не сыгран (выключен, браузер еще не дал звучать) или им нельзя управлять
    if (!sound || SOUND_CONTROLS.some((control) => typeof sound[control] !== 'function')) {
      return;
    }
    // Слышимость — с нуля: дальше ее поднимает update
    this.voices.push({sound, owner: group, level: 0});
    this.lastSoundId = soundId;
    this.sinceStartMs = 0;
  }

  /**
   * Случайный из загруженных звуков привязки — не тот, что в прошлый раз, если
   * есть из чего выбрать
   * @returns {string|null} null — ни один еще не загружен
   */
  pickSoundId() {
    const soundManager = this.scene?.soundManager;
    // Сцена не умеет сказать, загружен ли звук, — пробуем любой: незагруженный она молча не сыграет
    const canCheck = typeof soundManager?.checkIfDeferredSoundLoaded === 'function';
    const loaded = this.binding.soundIds.filter((soundId) => (
      !canCheck || soundManager.checkIfDeferredSoundLoaded(soundId)
    ));
    if (loaded.length === 0) {
      return null;
    }
    const fresh = loaded.filter((soundId) => soundId !== this.lastSoundId);
    const choice = fresh.length > 0 ? fresh : loaded;
    return choice[Math.min(choice.length - 1, Math.floor(this.rng() * choice.length))];
  }

  /** Оборвать звуки разом; появления остаются: те, что идут, заново не зазвучат */
  stop() {
    const {voices} = this;
    this.voices = [];
    voices.forEach(({sound}) => this.stopSound(sound));
  }

  stopSound(sound) {
    try {
      sound.stop();
    } catch (error) {
      console.warn('[FieldEffectSounds] Не удалось остановить звук эффекта подложки', error);
    }
  }

  /**
   * Поле перестроили: прежних групп больше нет — звук обрывается, появления
   * забываются. Пауза между началами звуков идет как шла: частая перестройка
   * поля (редактор) не должна шипеть на каждую
   */
  reset() {
    this.stop();
    this.appearances.length = 0;
  }
}

const createSound = (scene, binding, rng) => {
  if (binding?.kind === STORM) {
    return new StormSound(scene, binding);
  }
  return binding?.kind === GUST ? new GustSound(scene, binding, rng) : null;
};

export default class FieldEffectSounds {
  /**
   * @param {Phaser.Scene} scene — сцена с soundManager
   * @param {Object} [options]
   * @param {function(): number} [options.rng] — источник случайности (в тестах — заглушка)
   * @param {Function} [options.resolveBindings] — привязки подложки:
   *   (идентификатор) -> [{kind, tag, soundIds, resolveStrength?, …числа}]. По
   *   умолчанию — по перечням configs/fieldEffectSounds и построителей
   *   эффектов; в тестах — свои
   */
  constructor(scene, {rng = Math.random, resolveBindings = resolveRegisteredBindings} = {}) {
    this.scene = scene;
    this.rng = rng;
    this.resolveBindings = resolveBindings;
    this.groundBackgroundId = undefined;
    // Звуки привязок нынешней подложки: StormSound и GustSound
    this.sounds = [];
  }

  /**
   * Пейзаж лег под поле (или поле перестроили): звучит то, что положено этой
   * подложке. Все, что звучало, обрывается разом: прежней картинки больше нет.
   * Зовется ДО того, как эффект подложки строится заново: строясь, он уже
   * спрашивает, можно ли его дорожкам рождать группы (checkIfTrackAllowed).
   *
   * @param {string|null} [groundBackgroundId]
   */
  setGroundBackground(groundBackgroundId) {
    this.clear();
    // Подложка та же — привязки те же: помнят, что играло в прошлый раз и когда
    if (groundBackgroundId === this.groundBackgroundId) {
      return;
    }
    this.groundBackgroundId = groundBackgroundId;
    this.sounds = [];
    try {
      this.sounds = this.resolveBindings(groundBackgroundId)
        .map((binding) => createSound(this.scene, binding, this.rng))
        .filter(Boolean);
    } catch (error) {
      console.warn('[FieldEffectSounds] Не удалось завести звуки эффекта подложки', error);
      this.sounds = [];
    }
  }

  /**
   * Можно ли дорожке эффекта подложки рождать группы. «Нет» — только дорожке,
   * у которой на этой подложке звук «бури», и он еще не загружен: такая
   * картинка идет только вместе со своим звуком. Где звука нет вовсе (сцена
   * без звуковой подсистемы, демо-партия с выключенным звуком), ждать нечего.
   *
   * @param {{tag: (string|undefined)}} track — дорожка эффекта
   * @returns {boolean}
   */
  checkIfTrackAllowed(track) {
    try {
      const tag = track?.tag;
      if (tag === undefined || tag === null) {
        return true;
      }
      return this.sounds.every((sound) => (
        sound.binding.kind !== STORM || sound.binding.tag !== tag || sound.checkIfReady()
      ));
    } catch (error) {
      // Как и при сбое самой проверки звука (FieldWeatherSound.checkIfReady):
      // дорожка подождет и спросит снова
      console.warn('[FieldEffectSounds] Не удалось узнать, можно ли эффекту подложки начаться', error);
      return false;
    }
  }

  /**
   * Шаг времени: звуки идут за своими эффектами.
   *
   * @param {number} stepMs — шаг времени кадра (тот же, что двигает картинку)
   * @param {{findLiveGroups: Function}|null} [effects] — контроллер эффекта
   *   подложки (FieldBiomeEffects) после своего шага; нет его — эффектов нет,
   *   и все, что звучит, стихает
   */
  update(stepMs, effects) {
    for (let index = 0; index < this.sounds.length; index += 1) {
      const sound = this.sounds[index];
      try {
        sound.update(stepMs, effects?.findLiveGroups?.(sound.binding.tag) || NO_GROUPS);
      } catch (error) {
        // В консоль — раз на привязку, а не на каждом кадре
        if (!sound.isFailureReported) {
          sound.isFailureReported = true;
          console.warn('[FieldEffectSounds] Не удалось сыграть звук эффекта подложки', error);
        }
        sound.stop();
      }
    }
  }

  /** Эффект убрали с поля (поле перестроили): шагов времени может не быть — звуки обрываются разом */
  clear() {
    this.sounds.forEach((sound) => {
      sound.isFailureReported = false;
      sound.reset();
    });
  }

  /** Сцена уходит: звуки обрываются, новых не будет */
  destroy() {
    this.clear();
    this.sounds = [];
    this.groundBackgroundId = undefined;
  }
}
